import {
  Button,
  FileDropZone,
  focusOnMount,
  GroupLabel,
  Input,
} from "@coval/ui"
import { FileAudio, Link, Upload, X } from "lucide-react"

export type AudioSourceMode = "upload" | "link" | null
export type AudioUrlValidationStatus = "idle" | "valid" | "error"

interface AudioSourceFieldProps {
  audioFile: File | null
  audioFileError: string
  audioUrl: string
  disabled: boolean
  mode: AudioSourceMode
  validationError: string
  validationStatus: AudioUrlValidationStatus
  onFileChange: (file: File | null) => void
  onModeChange: (mode: Exclude<AudioSourceMode, null>) => void
  onReset: () => void
  onSaveUrl: () => void
  onUrlChange: (url: string) => void
}

export function AudioSourceField({
  audioFile,
  audioFileError,
  audioUrl,
  disabled,
  mode,
  validationError,
  validationStatus,
  onFileChange,
  onModeChange,
  onReset,
  onSaveUrl,
  onUrlChange,
}: AudioSourceFieldProps) {
  return (
    <section className="space-y-4 rounded-md border border-coval-border bg-surface-0 p-5">
      <div className="space-y-1">
        <GroupLabel>
          <span className="flex items-center gap-2">
            <FileAudio className="size-4 text-coval-text-muted" />
            Audio
          </span>
        </GroupLabel>
        <p className="text-xs text-coval-text-muted">WAV, MP3, or M4A</p>
      </div>

      {mode === null ? (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={disabled}
              onClick={() => onModeChange("upload")}
            >
              <Upload />
              Upload
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={disabled}
              onClick={() => onModeChange("link")}
            >
              <Link />
              URL
            </Button>
          </div>
        </div>
      ) : mode === "link" ? (
        validationStatus === "valid" ? (
          <div className="flex items-center gap-3 rounded-md border border-coval-border bg-surface-0 px-3 py-2.5">
            <span className="shrink-0 text-coval-text-muted">
              <Link className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-sm font-medium text-coval-text"
                title={audioUrl}
              >
                {audioUrl}
              </p>
              <p className="text-xs text-coval-text-muted">Linked audio</p>
            </div>
            <Button
              type="button"
              icon
              variant="ghost"
              tooltip="Remove linked audio"
              disabled={disabled}
              onClick={onReset}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  id="conversation-audio-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://example.com/audio.mp3"
                  value={audioUrl}
                  disabled={disabled}
                  error={validationStatus === "error"}
                  startAdornment={<Link />}
                  onClear={onReset}
                  onChange={(event) => onUrlChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      onSaveUrl()
                    }
                  }}
                  aria-label="Audio URL"
                  ref={focusOnMount}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0"
                  disabled={!audioUrl.trim() || disabled}
                  onClick={onSaveUrl}
                >
                  Save
                </Button>
              </div>
              {validationError && (
                <p role="alert" className="text-xs text-coval-danger-text">
                  {validationError}
                </p>
              )}
            </div>
          </div>
        )
      ) : audioFile ? (
        <div className="flex items-center gap-3 rounded-md border border-coval-border bg-surface-0 px-3 py-2.5">
          <span className="shrink-0 text-coval-text-muted">
            <FileAudio className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p
              className="truncate text-sm font-medium text-coval-text"
              title={audioFile.name}
            >
              {audioFile.name}
            </p>
            <p className="text-xs text-coval-text-muted">
              {audioFile.size < 1024 * 1024
                ? `${Math.max(1, Math.round(audioFile.size / 1024))} KB`
                : `${(audioFile.size / (1024 * 1024)).toFixed(1)} MB`}
            </p>
          </div>
          <Button
            type="button"
            icon
            variant="ghost"
            tooltip="Remove audio file"
            disabled={disabled}
            onClick={onReset}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <FileDropZone
            file={null}
            accept=".wav,.mp3,.m4a"
            label="Conversation audio file"
            fileIcon={<FileAudio />}
            disabled={disabled}
            className="py-5"
            onFileChange={onFileChange}
          />
          {audioFileError && (
            <p role="alert" className="text-xs text-coval-danger-text">
              {audioFileError}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
